import React, { createContext, useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';

// project import

// TODO: SHOULD BE PART OF MIX THIS MODULE AND AUTH MICROFRONT
import { JWTContextType, PasswordResetConfirmMutationInput } from 'model/types/auth';
import { authenticatedPost, setSession, unauthenticatedPost } from 'utils/axios';

// state-management
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { initialState, queryMerchantInfo, setBasicInfo } from 'app/slices/merchantSlice';
import { login as authLogin, logout as authLogout, prepareMerchantAttach, queryUser, setUser, userLogin } from 'app/slices/authSlice';

// queries
import { USER_REGISTER } from 'queries/user_register';
import { PASSWORD_RESET } from 'queries/password_reset';
import { PASSWORD_RESET_CONFIRM } from 'queries/password_reset_confirm';
import { ATTACH_MERCHANT_TO_SESSION } from 'queries/attatch_merchant_to_session';

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext<JWTContextType | null>(null);

export const JWTProvider = ({ children }: { readonly children: React.ReactNode }) => {
  const [authInProgress, setAuthInProgress] = useState(false);
  const { isRoleAttached, isLoggedIn, flags, hasCampaignPlan, tableVisibilityPrefs, user, userTokenInfo } = useAppSelector(
    (store) => store.auth
  );
  const dispatch = useAppDispatch();

  useEffect(() => {
    const init = async () => {
      try {
        setAuthInProgress(true);
        const token = window.localStorage.getItem('token');
        if (token) {
          await setCurrentUser(token);
          await getMerchantInfo();
        } else {
          logout();
        }
      } catch (err) {
        logout();
      } finally {
        setAuthInProgress(false);
      }
    };
    init();
  }, []);

  const login = async (email: string, password: string) => {
    const result = await userLogin(email, password);
    if (!result.errors) {
      dispatch(authLogin(result.authState));
      setCurrentUser();
    }
    return result;
  };

  const logout = () => {
    dispatch(authLogout());
    dispatch(setBasicInfo(initialState));
  };

  const setCurrentUser = async (token?: string) => {
    const authUser = await queryUser();
    dispatch(setUser({ ...authUser, token }));
  };

  async function getMerchantInfo() {
    try {
      const result = await queryMerchantInfo();
      dispatch(setBasicInfo(result));
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  const register = async (email: string, password: string, registrationToken: string | null) => {
    if (!registrationToken) return { errors: [{ message: 'Invalid registration token', path: ['userRegister'] }], userRegister: null };
    const response = await unauthenticatedPost({
      query: USER_REGISTER,
      vars: {
        email,
        password,
        registrationToken
      }
    });
    return response;
  };

  const resetPassword = async (email: string) => {
    let returnUrl = `${import.meta.env.VITE_SELLER_API_URL}reset-password`;
    if (isLoggedIn) {
      returnUrl = `${import.meta.env.VITE_SELLER_API_URL}profiles/account/password`;
    }
    const response = await unauthenticatedPost({
      query: PASSWORD_RESET,
      vars: {
        input: {
          email: email,
          returnUrl
        }
      }
    });
    return response;
  };

  const resetPasswordConfirm = async (input: PasswordResetConfirmMutationInput) => {
    const response = await unauthenticatedPost({
      query: PASSWORD_RESET_CONFIRM,
      vars: { input: input }
    });
    return response;
  };

  const merchantSwitch = async (merchantId: string) => {
    dispatch(prepareMerchantAttach());
    try {
      setAuthInProgress(true);

      const result = await authenticatedPost({
        query: ATTACH_MERCHANT_TO_SESSION,
        vars: {
          merchantId
        }
      });
      const token = result?.data?.attachMerchantToSession?.token;
      if (token) {
        // set session
        setSession(token);
        await setCurrentUser(token);
        await getMerchantInfo();
      } else {
        logout();
      }
    } catch (e) {
      Sentry.captureException(e);
    } finally {
      setAuthInProgress(false);
    }
  };

  const value = useMemo(
    () => ({
      authInProgress,
      isLoggedIn,
      isRoleAttached,
      flags,
      hasCampaignPlan,
      login,
      logout,
      merchantSwitch,
      register,
      resetPassword,
      resetPasswordConfirm,
      tableVisibilityPrefs,
      user,
      userRole: userTokenInfo?.role?.type ?? null
    }),
    [
      authInProgress,
      isLoggedIn,
      isRoleAttached,
      flags,
      hasCampaignPlan,
      login,
      logout,
      merchantSwitch,
      register,
      resetPassword,
      resetPasswordConfirm,
      tableVisibilityPrefs,
      user,
      userTokenInfo
    ]
  );

  return <JWTContext.Provider value={value}>{children}</JWTContext.Provider>;
};

export default JWTContext;
